import { apiUploadStorageFile } from '~/lib/api/storage';
import type { StorageKind } from '@shared/types/storage';

interface HiResPage {
  pageNumber: number;
  file: File;
}

interface BrochureArtifactsLike {
  pagePreviews: File[];
  pageHiRes: HiResPage[];
  embeddedImages: File[];
}

/**
 * Deduplicates uploads per File object so a retry after a partial failure
 * (e.g. one 429/404 out of ~56 parallel uploads) only re-sends the files that
 * failed — instead of re-uploading everything and tripping the per-endpoint
 * rate limit again. We cache the in-flight promise (not just the resolved
 * URL): the first send's Promise.all rejects on the first failure while the
 * other ~55 uploads are still running, so a retry that fired against resolved
 * URLs alone would restart those still-in-flight uploads. A rejected upload is
 * evicted so it can actually be retried. The cache is keyed on chatId as well
 * as File, since a fully-failed send mints a fresh chatId on retry — a URL
 * from the old chatId would point at the wrong storage prefix.
 */
const uploads = new WeakMap<File, { chatId: string; promise: Promise<string> }>();

function uploadOnce(chatId: string, file: File, kind?: StorageKind): Promise<string> {
  const cached = uploads.get(file);

  if (cached && cached.chatId === chatId) {
    return cached.promise;
  }

  const promise = apiUploadStorageFile(chatId, file, kind).then((result) => result.url);
  uploads.set(file, { chatId, promise });

  // Evict on failure so the file can be retried; guard on identity so we don't
  // drop a newer entry (e.g. a fresh-chatId upload) started after this one.
  promise.catch(() => {
    if (uploads.get(file)?.promise === promise) {
      uploads.delete(file);
    }
  });

  return promise;
}

/**
 * Upload every brochure artifact and return the text markers to prepend to the
 * stashed first message. All artifact kinds (including embedded images) use
 * ensureChatOwned-backed kinds so no upload can 404 racing the chat-row
 * creation.
 */
export async function uploadBrochureArtifacts(
  chatId: string,
  pdf: File,
  artifacts: BrochureArtifactsLike,
): Promise<string[]> {
  const [pdfUrl, pageUrls, hiRes, assetUrls] = await Promise.all([
    uploadOnce(chatId, pdf, 'brochure'),
    Promise.all(artifacts.pagePreviews.map((f) => uploadOnce(chatId, f, 'brochure-pages'))),
    Promise.all(
      artifacts.pageHiRes.map((p) =>
        uploadOnce(chatId, p.file, 'brochure-pages').then((url) => ({ pageNumber: p.pageNumber, url })),
      ),
    ),
    Promise.all(artifacts.embeddedImages.map((f) => uploadOnce(chatId, f, 'brochure-assets'))),
  ]);

  return [
    `[BROCHURE_PDF: ${pdfUrl}]`,
    `[BROCHURE_PAGES: ${pageUrls.join(', ')}]`,
    `[BROCHURE_PAGES_HIRES: ${hiRes.map((h) => `${h.pageNumber}=${h.url}`).join(', ')}]`,
    ...(assetUrls.length > 0 ? [`[BROCHURE_ASSETS: ${assetUrls.join(', ')}]`] : []),
  ];
}
